import React, { useState, useCallback, useContext } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { FormattedMessage } from 'react-intl';

import { AllocationRulesContext } from './context';
import EditableRuleBody from './editable-rule-body';
import messages from './messages';
import RemoveRuleConfirmation from './modals/remove-rule-confirmation';
import ReadOnlyRuleBody from './readonly-rule-body';
import { getRulePrioritizationText, getRuleNameFromEditorType } from './rule-helper';
import style from './style.module.less';
import './prop-types/allocation-rule.types';

/**
 * @param {object} props
 * @param {AllocationRuleType} props.rule
 * @param {(allocationRuleId: string) => void} props.onRuleRemoved
 * @returns {JSX.Element}
 */
const AllocationRule = ({ rule, onRuleRemoved }) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const onCloseConfirmation = useCallback(() => {
    setIsConfirmModalOpen(false);
  }, [setIsConfirmModalOpen]);
  const onOpenConfirmation = useCallback(() => {
    setIsConfirmModalOpen(true);
  }, [setIsConfirmModalOpen]);
  const onConfirmCloseAndRemoveRule = useCallback(() => {
    setIsConfirmModalOpen(false);
    onRuleRemoved(rule.allocationRuleId);
  }, [setIsConfirmModalOpen, onRuleRemoved, rule]);
  const { filterSets, sellerTags, marketPlaces, ruleIdInEdit, setRuleIdInEdit } = useContext(AllocationRulesContext);
  const hasAllocationRuleInEdit = ruleIdInEdit !== undefined;
  const { name, filterId, editorType } = rule;
  const ruleFilterSet = filterSets[filterId];
  const ruleName = getRuleNameFromEditorType(name, editorType, ruleFilterSet, sellerTags, marketPlaces);

  return (
    <Draggable key={rule.task} draggableId={`${rule.task}-${rule.name}`} index={rule.task} isDragDisabled={hasAllocationRuleInEdit}>
      {(dragProvided) => (
        <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps}>
          <div className={style['rule-wrapper']}>
            {isConfirmModalOpen ? <RemoveRuleConfirmation onClose={onCloseConfirmation} onConfirm={onConfirmCloseAndRemoveRule} /> : null}
            <div className={style['ordering-indicator']}>
              {rule.task === 1 && <FormattedMessage {...messages.allocationStrategiesOldestFirst} />}
              {rule.task > 1 && <FormattedMessage {...messages.allocationStrategiesOldestThen} />}
            </div>
            {ruleIdInEdit !== rule.allocationRuleId && (
              <ReadOnlyRuleBody
                allocationRuleId={rule.allocationRuleId}
                dragProvided={dragProvided}
                filterId={rule.filterId}
                hasAllocationRuleInEdit={hasAllocationRuleInEdit}
                onOpenConfirmation={onOpenConfirmation}
                ruleName={ruleName}
                rulePrioritizationText={getRulePrioritizationText(rule.sortDesc, rule.sortField)}
                setRuleIdInEdit={setRuleIdInEdit}
              />
            )}
            {ruleIdInEdit === rule.allocationRuleId && (
              <EditableRuleBody
                allocationRuleId={rule.allocationRuleId}
                dragProvided={dragProvided}
                filterId={rule.filterId}
                ruleName={ruleName}
                setRuleIdInEdit={setRuleIdInEdit}
              />
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};
export default AllocationRule;
